/**
 * OwnerReviewScreen — the owner sign-off gate (mockup #s-owner_review).
 *
 * Reads ONLY existing report state + the one new project-doc boolean
 * (`ownerReviewed`). The owner approves the draft before it renders; signing off
 * unlocks the report-render stage in the L1A derivation (deriveL1AStageStates
 * gates `rendered` behind owner review).
 *
 * EXPLICIT NOW / AUTO LATER (Rohit's ruling): the "Approve / sign off" control is
 * a manual write today. The L1A_STAGE_CONFIG.autoDerive seam flips owner_review
 * to report-state-driven later — when that flag is on, the derivation ignores the
 * boolean and mirrors the report status, and this manual control becomes a no-op
 * override (hide it then). The field name stays stable so the flip is a
 * write-source change, not a schema change.
 *
 * Writes go through the existing `updateProject` project-patch path (the
 * `ownerReviewed` key is whitelisted in projectStore.projectPatchBody).
 */
import { useState } from 'react';
import { Box, Btn3, Tag } from '../ui';
import { updateProject } from '../../lib/projectStore';
import type { ReportStatus } from '../../lib/reportDelivery';

export default function OwnerReviewScreen({
  pid,
  ownerReviewed,
  reportStatus,
  mayCommit,
  actorName,
  onMsg,
}: {
  pid: string;
  ownerReviewed: boolean;
  /** null = no report generated yet. */
  reportStatus: ReportStatus | null;
  mayCommit: boolean;
  actorName: string;
  onMsg: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const draftExists =
    reportStatus === 'draft' || reportStatus === 'approved' || reportStatus === 'delivered';

  async function setReviewed(next: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      await updateProject(pid, { ownerReviewed: next }, actorName);
      onMsg(next ? 'Draft signed off — delivery unlocked.' : 'Sign-off withdrawn.');
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const draftTag = !draftExists ? (
    <Tag>not generated</Tag>
  ) : reportStatus === 'draft' ? (
    <Tag kind="warn">draft ready</Tag>
  ) : (
    <Tag kind="val">approved</Tag>
  );

  return (
    <Box title="Draft review" note="The curation gate — owner approves before the report renders">
      <div className="l1a-screen">
        <div className="kv">
          <span className="k">Draft ready for review</span>
          {draftTag}
        </div>
        <div className="kv">
          <span className="k">Owner sign-off</span>
          {ownerReviewed ? <Tag kind="val">signed off</Tag> : <Tag>pending</Tag>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
        {!ownerReviewed ? (
          <Btn3
            intent="grn"
            size="sm"
            disabled={busy || !draftExists || !mayCommit}
            title={
              !draftExists
                ? 'Generate a draft first'
                : !mayCommit
                  ? 'Only the project assignee or an admin can sign off'
                  : 'Approve the draft so it can be delivered'
            }
            onClick={() => setReviewed(true)}
          >
            {busy ? 'Saving…' : 'Approve / sign off'}
          </Btn3>
        ) : (
          <>
            <span style={{ fontSize: 12.5, color: 'var(--good)' }}>✓ Signed off</span>
            <Btn3
              intent="ghost"
              size="sm"
              disabled={busy || !mayCommit}
              title="Withdraw the sign-off (reversible before delivery)"
              onClick={() => setReviewed(false)}
            >
              Undo sign-off
            </Btn3>
          </>
        )}
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '12px 0 0', lineHeight: 1.55 }}>
        Explicit sign-off today; designed to flip to automatic later (the report
        curation state drives it) — at which point this control is hidden and the
        status mirrors the report.
      </p>
    </Box>
  );
}
